import type { CSSProperties } from 'react'
import type { EasyModalProps, EasyModalState } from './declare'
import { useMemo, useEffect, useContext, createContext } from 'react'
import { reaction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { Modal } from 'antd'
import { $consts } from 'src/plugins'
import { useDomDraggable } from 'src/hooks'
import { uuid } from 'src/utils/tools'

const ModalPropsContext = createContext<EasyModalProps>({} as EasyModalProps)
const ModalContext = createContext<EasyModalState>({} as EasyModalState)

const ModalMain = observer(() => {
  const props = useContext(ModalPropsContext)
  const state = useContext(ModalContext)

  const handleOk = (evt: any) => {
    if (props.modal?.onOk instanceof Function) {
      const promise: any = props.modal.onOk(evt)
      if (promise instanceof Promise) {
        promise.then(() => { state.setVisible(false) })
      } else {
        state.setVisible(!!promise)
      }
    } else {
      state.setVisible(false)
    }
  }

  const handleCancel = (evt: any) => {
    props.modal?.onCancel?.(evt)
    state.setVisible(false)
  }

  const draggable = useMemo<boolean>(() => {
    const { dragable = true } = props
    return !!(dragable && state.visible)
  }, [props.dragable, state.visible])

  useDomDraggable(draggable, state.uniqueClass, 'ant-modal-header', {
    onInit: (wrapperDom, dragDom) => {
      if (wrapperDom.parentElement) {
        // 防止拖出视窗时，界面滚动
        wrapperDom.parentElement.style.overflow = 'hidden'
      }
      dragDom.className = `${dragDom.className} cursor-pointer`
    },
    onDrag: (wrapperDom, dragDom, position) => {
      wrapperDom.style.margin = '0px'
      wrapperDom.style.left = `${position.offsetLeft}px`
      wrapperDom.style.top = `${position.offsetTop}px`
    }
  })

  const { fullModal } = props
  const presetMaxWidth = $consts['APP/MODAL_DEFAULT_MAX_WIDTH']
  return (
    <Modal
      destroyOnClose
      maskClosable={false}
      footer={null}
      style={{ maxWidth: presetMaxWidth }}
      {...props.modal}
      {...state.modal}
      width={fullModal ? '100%' : (props.modal?.width || state.modal.width)}
      open={state.visible}
      onOk={handleOk}
      onCancel={handleCancel}
      className={`${state.uniqueClass} ${props.className} ${fullModal ? 'full-modal' : ''}`}
    >
      {props.children}
    </Modal>
  )
})

const ModalSlot = observer(() => {
  const props = useContext(ModalPropsContext)
  const state = useContext(ModalContext)

  const handleClick = () => {
    !props.disabled && state.setVisible(true)
  }

  const className = useMemo(() => {
    return `form-modal__slot ${props.slotClass}`
  }, [props.slotClass])

  const style = useMemo<CSSProperties>(() => {
    return {
      display: 'inline-block',
      lineHeight: 1,
      cursor: 'pointer',
      ...props.slotStyle
    }
  }, [props.slotStyle])

  return <div onClick={handleClick} className={className} style={style}>{props.slot}</div>
})

const EasyModal = (props: EasyModalProps) => {
  const state = useLocalObservable<EasyModalState>(() => {
    const presetWidth = $consts['APP/MODAL_DEFAULT_WIDTH']
    return {
      initDone: true,
      uniqueClass: uuid(),
      modal: { width: presetWidth },
      updateModal (modal) {
        Object.assign(this.modal, modal)
      },
      visible: false,
      setVisible (visible) {
        this.visible = visible
      },
      show () {
        this.setVisible(true)
      },
      close (data?: any) {
        this.setVisible(false)
        props.onClose?.(data) // 用于一些弹窗关闭需要回调的场景用
      },
    }
  })

  useEffect(() => {
    state.setVisible(!!props.visible)

    const dispose = reaction(
      () => state.visible,
      () => props.onVisibleChange?.(state.visible)
    )

    return () => dispose()
  }, [props.visible])

  return (
    <ModalPropsContext.Provider value={props}>
      <ModalContext.Provider value={state}>
        <ModalSlot />
        <ModalMain />
      </ModalContext.Provider>
    </ModalPropsContext.Provider>
  )
}

const useModalContext = () => {
  return useContext(ModalContext)
}

export { ModalContext, useModalContext }
export default EasyModal